

import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import shipimg from "@/assets/shipimg.svg";

const StepperBoundariesChart = ({ axisData
}) => {
  const { mode, years, boundaries, ciiData } = axisData
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const decimals = 2
  /** -------------------------
   * Auto yMin & yMax
   ------------------------- */
  const { yMin, yMax } = useMemo(() => {
    try {
      const boundaryVals = Object.values(boundaries).flat().filter(v => typeof v === "number");
      const ciiVals = Object.values(ciiData).flat().map(item => typeof item === "object" ? item.value : item);
      const allValues = [...boundaryVals, ...ciiVals].filter(v => !isNaN(v));

      if (!allValues.length) return { yMin: 0, yMax: 10 };
      return {
        yMin: Math.floor(Math.min(...allValues)),
        yMax: Math.ceil(Math.max(...allValues))
      };
    } catch {
      return { yMin: 0, yMax: 10 };
    }
  }, [boundaries, ciiData]);


  /** -------------------------
 * Get maximum CII value to determine red boundary extension
 ------------------------- */
  const getMaxCiiValue = useMemo(() => {
    const ciiVals = ciiData && Object.values(ciiData)?.flat().map(item => typeof item === "object" ? item.value : item);
    return ciiVals?.length ? Math.max(...ciiVals?.filter(v => !isNaN(v) && v !== null)) : [];
  }, [ciiData]);


  /** -------------------------
   * Create stacked bar segments for given year
   ------------------------- */
  const createStackedBars = (year) => {
    const b = boundaries[year] || [];
    if (b.length < 5) return [0, 0, 0, 0, 0];

    // Check if any CII value exceeds the highest boundary (E level)
    const shouldExtendRed = getMaxCiiValue > b[4];
    const redExtension = shouldExtendRed ? getMaxCiiValue - b[4] : 0;

    return [
      b[0],                    // A: 0 to b[0]
      b[1] - b[0],            // B: b[0] to b[1] 
      b[2] - b[1],            // C: b[1] to b[2]
      b[3] - b[2],            // D: b[2] to b[3]
      (b[4] - b[3]) + redExtension  // E: b[3] to b[4] + extension if needed
    ];
  };

  /** -------------------------
   * Generate labels based on mode
   ------------------------- */
  const labels = useMemo(() => {
    if (mode === "voyage") {
      return years.flatMap(year => (ciiData[year] || []).map(v => v.voyage));
    }
    if (mode === "monthly") {
      return years.flatMap(year => months.map(month => `${month} ${year}`));
    }
    return years;
  }, [mode, years, months, ciiData]);

  /** -------------------------
   * Build series (stacked bars + CII line)
   ------------------------- */
  const series = useMemo(() => {
    const segments = ["A", "B", "C", "D", "E"];

    // Bar segments
    const barSeries = segments.map((seg, i) => ({
      name: seg,
      type: "bar",
      data: labels?.length ? labels?.map(label => {
        if (mode === "yearly") {
          return createStackedBars(label)[i];
        } else if (mode === "monthly") {
          const year = parseInt(label.split(" ")[1], 10);
          return createStackedBars(year)[i];
        } else {
          // voyage
          const year = years.find(y => (ciiData[y] || []).some(v => v.voyage === label));
          return year ? createStackedBars(year)[i] : 0;
        }
      }) : [],
    }));

    // CII line series
    const ciiSeries = {
      name: "CII",
      type: "line",
      data:
        mode === "yearly"
          ? years.map(year => ciiData[year] ?? null)
          : mode === "monthly"
            ? labels?.map(label => {
              const [month, year] = label.split(" ");
              const match = (ciiData[year] || []).find(item => item.month === month);
              return match ? match.value : null;
            })
            : labels?.map(voyage => {
              const year = years.find(y => (ciiData[y] || []).some(v => v.voyage === voyage));
              const match = (ciiData[year] || []).find(item => item.voyage === voyage);
              return match ? match.value : null;
            }),
    };

    return [...barSeries, ciiSeries];
  }, [labels, years, mode, boundaries, ciiData]);
  /** -------------------------
   * Chart Annotations (ship icon)
   ------------------------- */
  const getAnnotations = useMemo(() => {
    const ciiSeriesData = series.find(s => s.name === "CII")?.data || [];
    const points = ciiSeriesData
      .map((value, index) =>
        value !== null
          ? {
            x: labels[index],
            y: value,
            image: { path: shipimg, width: 25, height: 25, offsetY: -20 },
          }
          : null
      )
      .filter(Boolean);

    const textAnnotations = ciiSeriesData
      .map((value, index) =>
        value !== null
          ? {
            x: labels[index],
            y: yMin, // Position at the bottom of the chart
            text: value.toFixed(decimals),
            yAxisIndex: 0,
            style: {
              color: '#1f77b4', // Blue color to match CII
              fontSize: '12px',
              fontWeight: 'bold',
              background: 'rgba(255,255,255,0.9)',
              borderRadius: '3px',
              padding: {
                left: 6,
                right: 6,
                top: 3,
                bottom: 3
              }
            },
            offsetY: 15 // Small offset from the very bottom
          }
          : null
      )
      .filter(Boolean);

    return {
      points: points,
      texts: textAnnotations
    };
  }, [series, labels, decimals, yMin]);

  /** -------------------------
   * Chart options
   ------------------------- */
  const options = useMemo(() => ({
    chart: {
      stacked: true,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        columnWidth: "100%",  // Increased from 100% to make bars wider
        distributed: false,  // Keep bars grouped, not distributed
        rangeBarOverlap: false,
        rangeBarGroupRows: false,

      },
    },
    stroke: {
      curve: "smooth",
      width: [0, 0, 0, 0, 0, 3],
    },
    colors: ["#2ca02c", "#98df8a", "#ffdb58", "#ff7f0e", "#d62728", "#1f77b4"],
    xaxis: {
      categories: labels,
      labels: {
        rotate: mode === "monthly" || mode === "voyage" ? -45 : 0,
        style: { fontSize: mode === "monthly" || mode === "voyage" ? "10px" : "12px" },
      },
      tickPlacement: "between", // Changed from conditional to always "between"
      axisBorder: {
        show: true,
        color: '#78909C',
        offsetX: 0,
        offsetY: 0
      },
      axisTicks: {
        show: true,
        borderType: 'solid',
        color: '#78909C',
        height: 6,
        offsetX: 0,
        offsetY: 0
      },
    },

    tooltip: {
      enabled: false,
    }
    ,
    yaxis: {
      min: yMin,
      max: yMax,
      tickAmount: 12,
      labels: {
        style: { fontSize: "12px" },
        formatter: (val) => Number(val).toFixed(decimals),
      },
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [5], // Only enable for the 6th series (index 5), which is the CII line
      formatter: function (val, opts) {
        // Only show labels for CII series and when value is not null
        if (opts.seriesIndex === 5 && val !== null) {
          return Number(val).toFixed(decimals);
        }
        return '';
      },
      style: {
        colors: ['#1f77b4'], // Blue color for CII labels
        fontSize: '12px',
        fontWeight: 'bold'
      },
      background: {
        enabled: true,
        foreColor: '#fff',
        borderRadius: 3,
        padding: 6,
        opacity: 0.9
      },
      offsetY: 10 // Move labels down by 10 pixels
    },
    annotations: {
      points: getAnnotations.points,
      texts: getAnnotations.texts,
    },
    legend: { position: "top" },


  }), [labels, yMin, yMax, decimals, mode, getAnnotations]);

  return (<div style={{ width: '100%' }}>
    <Chart options={options} series={series} height={400} />
  </div>)
};

export default StepperBoundariesChart;
